import React, { useState, useEffect, useCallback } from 'react';
import Section6Matrix from '../components/Section6Matrix';   // section 540: the Section 6 terms, round by round
import AgreementMatrices from '../components/AgreementMatrices';
import { PLATFORM_NAME } from '../installation';   // ruling 312
import { DEFAULT_CRAFT } from '../data/craftTerms';   // ruling 312 phase 1 (c)
import { SlidesButton } from '../components/SlideDeck';   // ruling 311: an agreement's dated rule changes as slides
import { ruleChangesDeck } from '../data/slideDecks';
import { AMTRAK_BASE_RULES, AMTRAK_RULE_MODS } from '../data/ruleCatalog';   // ruling 310: matrix entries the GC confirmed and released
import { AGREEMENTS, REFERENCE_GUIDES, sharedAgreementFor } from '../data/agreementData';
import { heldFor, stateWords, GC_ACTS } from '../data/heldDocuments';   // Bob 2026-10-25: the GC signs off, replaces or removes - so it must be on his shelf
import { guideText } from '../data/referenceGuideText';
import { db } from '../firebase';
import { getDocs, doc, getDoc } from 'firebase/firestore';
import { visibleClaimQueries } from '../data/claimQueries';   // section 412: the audit log asks the way every other claims listing asks
import {
  loadAgreementRecords, loadNationalAgreements, scopeAgreements, canManage, currentVersion, fileUrl,
  uploadVersion, createAgreement, archiveAgreement, restoreAgreement, keyForCodeAgreement, recordRatification,
} from '../data/agreementsRepo';
import { ATTESTATION_TEXT, ATTESTATION_REQUIRED } from '../data/agreementCustody';   // Record 599: ruling 211 attestation at upload
import { RATIFICATION_KINDS, planRatification, ratificationState, mayRecordRatification } from '../data/ratification';   // WO-001 S10 (section 249): SS85 - the poll before the signature, the report to the Locals in 30 days
import { AGREEMENT_UNCONFIRMED_TEXT } from '../data/agreementStatus';   // ruling 324 (section 760)
import AgreementDocument from '../components/AgreementDocument';   // section 764: each agreement, rule by rule
import { documentForAgreement, buildAgreementDocument } from '../data/agreementDocument';

// Agreement Library — role-aware.
//   • Members & Local Chairs see a read-only, scoped view of their own agreement(s).
//   • The custodian General Chair (committee owner) and admin get the full lifecycle:
//     add a new agreement, swap in a new version (prior kept + archived), archive/restore,
//     forward, and view version history. Enforced server-side by firestore/storage rules;
//     the UI mirrors it. Files open in an in-app viewer — no external links.

export default function Agreements({ user, onNavigate }) {
  const [filter, setFilter] = useState('All');
  const [expandedLocations, setExpandedLocations] = useState({});
  const [showArchived, setShowArchived] = useState(false);
  const [openGuide, setOpenGuide] = useState(null);   // { title, text }
  const [records, setRecords] = useState({});
  const [memberRailroads, setMemberRailroads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [national, setNational] = useState(null);   // ruling 263: NCCC national agreements, read by everyone

  // modals / inline forms
  const [viewer, setViewer] = useState(null);          // { title, url, fileName }
  const [readDoc, setReadDoc] = useState(null);        // section 764: the rule-by-rule reading open in the Modal
  const [history, setHistory] = useState(null);        // display agreement
  const [swapFor, setSwapFor] = useState(null);        // display agreement
  const [swapEff, setSwapEff] = useState('');
  const [swapAttest, setSwapAttest] = useState(false);   // Record 599: ruling 211 attestation, ticked per upload
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ title: '', railroadKey: '', craft: DEFAULT_CRAFT, effectiveDate: '', committee: '' });
  const [auditAgreement, setAuditAgreement] = useState(null);
  const [auditClaims, setAuditClaims] = useState([]);
  const [loadingAudit, setLoadingAudit] = useState(false);
  // A3: null means the query answered. A string means it did NOT, and carries why.
  const [auditError, setAuditError] = useState(null);
  const [ratForm, setRatForm] = useState(null);   // S10: { versionNo, kind, polledOn, votesFor, votesAgainst, signedOn, reportedOn, evidence, reason }
  const [ratErr, setRatErr] = useState('');

  const isAdmin = user?.isAdmin || user?.role === 'admin';
  const isGCorAdmin = isAdmin || user?.role === 'general_chair';

  const reloadRecords = useCallback(async () => {
    const recs = await loadAgreementRecords(user);   // ruling 192: only the reader's committee
    setRecords(recs);
  }, [user && user.uid, user && user.committee]);

  useEffect(() => { if (user) loadNationalAgreements().then(setNational).catch(() => setNational([])); }, [user && user.uid]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    (async () => {
      setLoading(true);
      await reloadRecords();
      if (user && (user.role === 'member' || user.role === 'tester' || user.role === 'local_chair')) {
        try {
          const snap = await getDoc(doc(db, 'users', user.uid));
          const p = snap.exists() ? snap.data() : {};
          const rr = Array.isArray(p.railroads) ? p.railroads : (p.railroad ? [p.railroad] : []);
          setMemberRailroads(rr);
        } catch (e) { /* fall back to unscoped */ }
      }
      setLoading(false);
    })();
  }, [user, reloadRecords]);

  // Merged display list: code agreements (never empty) + custom records.
  const codeItems = AGREEMENTS.map(a => {
    const key = keyForCodeAgreement(a.id);
    return { ...a, key, source: 'code', record: records[key] || null };
  });
  const customItems = Object.values(records)
    .filter(r => r.source === 'custom')
    .map(r => ({
      id: r.key, key: r.key, source: 'custom',
      committee: r.committee, carrier: r.carrier, railroadKey: r.railroadKey,
      title: r.title, effective: r.effectiveDate, amended: null,
      status: r.status === 'archived' ? 'archived' : 'active',
      documents: Array.isArray(r.versions) ? r.versions.length : 0,
      description: '', locations: [], record: r,
    }));
  const allItems = codeItems.concat(customItems);

  const scoped = scopeAgreements(allItems, user, memberRailroads);
  const committees = ['All', ...Array.from(new Set(scoped.map(a => a.committee)))];
  const filtered = scoped.filter(a => filter === 'All' || a.committee === filter);
  // Archived agreements are retired, not deleted. They are kept out of the working list
  // and reachable in their own folder below it, so the list a custodian works from stays
  // the list of agreements actually in force. The committee filter applies to both.
  const activeItems = filtered.filter(a => a.status !== 'archived');
  const archivedItems = filtered.filter(a => a.status === 'archived');

  const toggleLocations = id => setExpandedLocations(prev => ({ ...prev, [id]: !prev[id] }));

  // ---- actions --------------------------------------------------------------
  const openViewer = async (item) => {
    const rec = item.record;
    const v = rec ? currentVersion(rec) : null;
    if (!v) { setViewer({ title: item.title, url: null, fileName: null }); return; }
    setBusy('view:' + item.key);
    const url = await fileUrl(v);
    setBusy('');
    setViewer({ title: item.title, url, fileName: v.fileName });
  };

  const doSwap = async (item, file) => {
    if (!file) return;
    if (!swapAttest) { alert(ATTESTATION_REQUIRED); return; }   // Record 599: ruling 211
    setBusy('swap:' + item.key);
    try {
      await uploadVersion(user, item, file, swapEff, swapAttest);
      await reloadRecords();
      setSwapFor(null); setSwapEff(''); setSwapAttest(false);
    } catch (e) { alert('Upload failed: ' + e.message); }
    finally { setBusy(''); }
  };

  const doArchive = async (item) => {
    if (!item.record) { alert('Nothing to archive yet — this agreement has no uploaded version.'); return; }
    if (!window.confirm('Archive "' + item.title + '"? It moves out of the active list but is kept in full — nothing is deleted.')) return;
    setBusy('arch:' + item.key);
    try { await archiveAgreement(user, item.key); await reloadRecords(); }
    catch (e) { alert('Archive failed: ' + e.message); }
    finally { setBusy(''); }
  };

  const doRestore = async (item) => {
    setBusy('arch:' + item.key);
    try { await restoreAgreement(user, item.key); await reloadRecords(); }
    catch (e) { alert('Restore failed: ' + e.message); }
    finally { setBusy(''); }
  };

  const doForward = async (item) => {
    const rec = item.record;
    const v = rec ? currentVersion(rec) : null;
    const url = v ? await fileUrl(v) : null;
    const subject = encodeURIComponent('Agreement — ' + item.title);
    const body = encodeURIComponent(
      'Sharing the current ' + item.title + (item.effective ? ' (effective ' + item.effective + ')' : '') + '.\n\n' +
      (url ? 'Document link (requires platform access):\n' + url + '\n\n' : '') +
      'Sent from ' + PLATFORM_NAME + '.'
    );
    // Opens the user's own mail client, pre-filled — the user sends it, not the app.
    window.location.href = 'mailto:?subject=' + subject + '&body=' + body;
  };

  const doCreate = async () => {
    if (!addForm.title.trim()) { alert('Title is required.'); return; }
    if (!addForm._file) { alert('Choose the agreement file to upload.'); return; }
    if (addForm._attested !== true) { alert(ATTESTATION_REQUIRED); return; }   // Record 599: ruling 211
    const cmte = (addForm.committee || user?.committee || '').trim();
    if (!cmte) { alert('Choose the custodian committee for this agreement.'); return; }
    setBusy('create');
    try {
      await createAgreement(user, {
        title: addForm.title.trim(),
        committee: cmte,
        railroadKey: addForm.railroadKey.trim(),
        carrier: addForm.railroadKey.trim(),
        craft: addForm.craft.trim() || DEFAULT_CRAFT,
      }, addForm._file, addForm.effectiveDate, addForm._attested === true);
      await reloadRecords();
      setShowAdd(false);
      setAddForm({ title: '', railroadKey: '', craft: DEFAULT_CRAFT, effectiveDate: '', committee: '' });
    } catch (e) { alert('Create failed: ' + e.message); }
    finally { setBusy(''); }
  };

  const handleAuditLog = async (agreement) => {
    setAuditAgreement(agreement); setLoadingAudit(true); setAuditClaims([]); setAuditError(null);
    try {
      // SECTION 412, 2026-09-25. This was `getDocs(collection(db, 'claims'))` - EVERY claim on the
      // platform, pulled into the browser and filtered here. Firestore does not filter a listing
      // against its rules, it PROVES the listing, and the claims read rule turns on
      // resource.data.committee / scopeKey - so an unfiltered listing cannot be proved for anyone
      // but the administrator. MEASURED, not reasoned: scripts\audit-screen-queries.js signed in
      // with a real General Chairperson token and this exact query came back HTTP 403.
      //
      // Which means the agreement audit log worked every time Bob opened it and failed for every
      // General Chairperson who did - the precise shape of defect that cost this platform the
      // trust of its officers. A3 (2026-09-22) fixed how the failure RENDERS; it did not fix the
      // query, because the query was never looked at.
      //
      // visibleClaimQueries() is the one place a claims listing is shaped (SS XI) and already
      // carries the status filter ruling 56 requires - the lesson of finding F-15. Admin still
      // gets the unfiltered listing, because for him it is provable.
      const qs = visibleClaimQueries(db, user);
      const snaps = await Promise.all(qs.map(q => getDocs(q)));
      const seen = new Map();
      snaps.forEach(sn => sn.docs.forEach(d => seen.set(d.id, { id: d.id, ...d.data() })));
      const all = [...seen.values()];
      const matched = all.filter(c => {
        const ag = c.applicableAgreement || c.agreement || '';
        return ag.toLowerCase().startsWith((agreement.title || '').toLowerCase());
      }).sort((a, b) => (b.filedAt?.seconds || 0) - (a.filedAt?.seconds || 0));
      setAuditClaims(matched);
    } catch (e) {
      // A3 (audit 2026-09-22). This logged to the console and left auditClaims empty, so a REFUSED
      // READ rendered as "No claims filed under this agreement" - a General Chairperson's audit log
      // showing him an empty page and looking truthful. An unanswered question is not an empty
      // answer, and this is the screen an officer uses to decide an agreement is quiet.
      console.error('Audit query failed:', e);
      setAuditError((e && (e.code || e.message)) || 'the claims read was refused');
    }
    finally { setLoadingAudit(false); }
  };

  // ---- render ---------------------------------------------------------------
  const roleLabel = isGCorAdmin ? 'custodian view — full lifecycle' : 'your agreements — read only';

  // Card renderer, shared by the active list and the Archived folder so the two
  // cannot drift apart (SS XI: one card, one definition).
  const renderAgreementCard = (agreement) => {
    const rec = agreement.record;
    const cur = rec ? currentVersion(rec) : null;
    const manage = canManage(user, agreement);
    const archived = agreement.status === 'archived';
    return (
      <div className="card" key={agreement.key} style={archived ? { opacity: 0.6 } : null}>
        <div className="card-header">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, color: '#E8A020', textTransform: 'uppercase' }}>{agreement.committee}</span>
              <span style={{ fontSize: 11, color: '#4A6280' }}>·</span>
              <span style={{ fontSize: 11, color: '#8AA4BF' }}>{agreement.carrier}</span>
              <span className={`badge ${archived ? 'badge-intake' : 'badge-settlement'}`}>{archived ? 'archived' : 'active'}</span>
              {rec && <span style={{ fontSize: 10, color: '#8AA4BF' }}>v{rec.currentVersion}</span>}
            </div>
            <div className="card-title">{agreement.title}</div>
            <div className="card-subtitle">
              Effective: {(cur && cur.effectiveDate) || agreement.effective || '—'}
              {agreement.amended && ` · Amended: ${agreement.amended}`}
              {cur && ` · current file: ${cur.fileName}`}
            </div>
          </div>
        </div>

        {agreement.description && <p style={{ fontSize: 13, color: '#8AA4BF', lineHeight: 1.6, marginBottom: 16 }}>{agreement.description}</p>}

        {/* G7.1 (section 224): a shared instrument says so - shared AGREEMENT, one custodian per territory, never a shared office. */}
        {agreement.source === 'code' && (() => {
          const s = sharedAgreementFor(agreement.id);
          if (!s) return null;
          return (
            <div style={{ fontSize: 12, color: '#8AA4BF', border: '1px solid #2E4A6B', background: '#0A1628', borderRadius: 6, padding: '8px 12px', marginBottom: 12 }}>
              {s.kind === 'single_system'
                ? <>Single System Agreement: one instrument, four territories. This territory's custodian is <strong style={{ color: '#E8A020' }}>{s.custodian}</strong>; the same instrument is custodied for their own territories by {s.sharedWith.join(', ')}. Custodianship is of the territory, not the instrument - no committee acts for another.</>
                : <>Roster note: <strong style={{ color: '#E8A020' }}>{s.party}</strong> is given on the 29 July roster as sharing this agreement with {s.custodian}. {s.confirmed ? 'Confirmed ' + s.confirmed + '.' : 'UNCONFIRMED - no chairman has yet defined the relationship; nothing on this platform acts on it.'}</>}
            </div>
          );
        })()}

        {/* Work Locations */}
        {agreement.locations && agreement.locations.length > 0 && (
          <div style={{ background: '#0A1628', border: '1px solid #2E4A6B', borderRadius: 6, padding: '10px 14px', marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 11, color: '#8AA4BF', textTransform: 'uppercase', letterSpacing: 1 }}>{agreement.locations.length} Work Location{agreement.locations.length !== 1 ? 's' : ''}</span>
              <button className="btn btn-secondary btn-sm" onClick={() => toggleLocations(agreement.key)}>{expandedLocations[agreement.key] ? 'Hide' : 'Show'}</button>
            </div>
            {expandedLocations[agreement.key] && (
              <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {agreement.locations.map((loc, i) => (
                  <span key={i} style={{ fontSize: 11, background: '#1B2A4A', border: '1px solid #2E4A6B', borderRadius: 4, padding: '3px 8px', color: '#A8C4DF' }}>{loc}</span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-secondary btn-sm" onClick={() => openViewer(agreement)} disabled={busy === 'view:' + agreement.key}>
            {busy === 'view:' + agreement.key ? 'Opening…' : (cur ? '📖 Read Agreement' : '📄 View Documents')}
          </button>
          {/* Section 764: the agreement rule by rule, with its source pages and its gaps named. */}
          {documentForAgreement(agreement.id) && (
            <button className="btn btn-secondary btn-sm" onClick={() => setReadDoc(buildAgreementDocument(documentForAgreement(agreement.id)))}>📑 Agreement text, rule by rule</button>
          )}
          {rec && (rec.versions || []).length > 0 && (
            <button className="btn btn-secondary btn-sm" onClick={() => setHistory(agreement)}>🕘 History ({(rec.versions || []).length})</button>
          )}
          {(isGCorAdmin || user?.role === 'local_chair') && (
            <button className="btn btn-secondary btn-sm" onClick={() => handleAuditLog(agreement)}>📋 Audit Log</button>
          )}

          {manage && (<>
            {swapFor && swapFor.key === agreement.key ? (
              <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', background: '#0A1628', border: '1px solid #2E4A6B', borderRadius: 6, padding: '4px 8px' }}>
                {/* Record 599: ruling 211 - the required attestation, before the file can be chosen */}
                <label style={{ fontSize: 11, color: '#DCE7F2', display: 'flex', gap: 6, alignItems: 'flex-start', maxWidth: 420, flexBasis: '100%' }}>
                  <input aria-label="Swap attest" type="checkbox" required checked={swapAttest} onChange={e => setSwapAttest(e.target.checked)} style={{ marginTop: 2 }} />
                  <span>{ATTESTATION_TEXT}</span>
                </label>
                <label style={{ fontSize: 10, color: '#8AA4BF' }}>effective</label>
                <input aria-label="effective" type="date" className="form-input" style={{ padding: '2px 6px', fontSize: 11 }} value={swapEff} onChange={e => setSwapEff(e.target.value)} />
                <label className="btn btn-primary btn-sm" style={{ cursor: swapAttest ? 'pointer' : 'not-allowed', opacity: swapAttest ? 1 : 0.5 }}>
                  {busy === 'swap:' + agreement.key ? 'Uploading…' : 'Choose file'}
                  <input aria-label="Attach the replacement agreement" type="file" style={{ display: 'none' }} disabled={!swapAttest} onChange={e => doSwap(agreement, e.target.files[0])} />
                </label>
                <button className="btn btn-secondary btn-sm" onClick={() => { setSwapFor(null); setSwapEff(''); setSwapAttest(false); }}>✕</button>
              </span>
            ) : (
              <button className="btn btn-secondary btn-sm" onClick={() => { setSwapFor(agreement); setSwapEff(''); setSwapAttest(false); }}>⤒ Swap in new version</button>
            )}
            <button className="btn btn-secondary btn-sm" onClick={() => doForward(agreement)}>✉ Forward</button>
            {archived
              ? <button className="btn btn-secondary btn-sm" onClick={() => doRestore(agreement)}>♻ Restore</button>
              : <button className="btn btn-secondary btn-sm" onClick={() => doArchive(agreement)} disabled={busy === 'arch:' + agreement.key}>🗄 Archive</button>}
          </>)}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="topbar">
        <div>
          <h1 className="topbar-title">Agreement Library</h1>
          <div className="topbar-meta">
            {loading ? 'Loading…' : `${activeItems.length} agreement${activeItems.length !== 1 ? 's' : ''} — ${roleLabel}${archivedItems.length ? ` · ${archivedItems.length} archived` : ''}`}
          </div>
        </div>
        {isGCorAdmin && (
          <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)}>➕ Add New Agreement</button>
        )}
      </div>

      <div className="page-content">
        {/* Ruling 306 item 3: the right shown where the platform already honours it (LMRDA Section 104). */}
        <div style={{ fontSize: 12, color: '#8AA4BF', marginBottom: 12 }}>You have the right to read the agreements that cover your work - they are here.{onNavigate ? <button type="button" style={{ color: '#E8A020', cursor: 'pointer', marginLeft: 6, background: 'transparent', border: 'none', padding: 0, font: 'inherit', textDecoration: 'underline' }} onClick={() => onNavigate('member-rights')}>Your rights as a union member</button> : null}</div>
        {/* Ruling 324 (section 760): no agreement is presented as accurate until its General Chairman signs off or replaces it. */}
        <div style={{ fontSize: 12, color: '#FFD27A', border: '1px solid #8A6A1F', borderRadius: 6, padding: '6px 10px', marginBottom: 12 }}>{AGREEMENT_UNCONFIRMED_TEXT}</div>
        {/* BOB, 2026-10-25: "every gc has to either approve what we already have, or delete, add, or swap out
            copies yes? Sign off or nge?" - and he was right that nothing on this platform is confirmed
            (ruling 324), so "unconfirmed" was never a reason to keep a held document off a committee’s
            shelf. Measured by scripts\scan-held-documents.js, listed by src\data\heldDocuments.js. */}
        {(() => {
          const mine = heldFor(user && user.committee);
          if (!mine.length) return null;
          return (
            <div className="card" style={{ marginBottom: 20, borderColor: '#8A6A1F' }}>
              <div className="card-title">Documents held for {user.committee} &mdash; waiting on the General Chairperson</div>
              <div style={{ fontSize: 12, color: '#8AA4BF', margin: '4px 0 10px' }}>
                These are in the platform’s keeping for this committee. <b>Not one of them has been read by a
                person and not one has been signed off</b>, which is also true of every agreement catalogued
                above &mdash; the General Chairperson signs off on a copy, replaces it, or removes it.
              </div>
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr><th>Document</th><th>Pages</th><th>What the platform can do with it</th><th>Where it came from</th></tr>
                  </thead>
                  <tbody>
                    {mine.map((d) => (
                      <tr key={d.file}>
                        <td style={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'nowrap' }}>
                          {d.file}
                          {d.national ? <span style={{ color: '#8AA4BF' }}> (national)</span> : null}
                          <div style={{ color: '#4A6280', fontSize: 10 }}>sha-256 {String(d.sha256).slice(0, 12)}</div>
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>{d.pages}</td>
                        <td style={{ fontSize: 12, color: '#FFD27A' }}>{stateWords(d)}</td>
                        <td style={{ fontSize: 11, color: '#8AA4BF', wordBreak: 'break-all' }}>{d.url || '[not recorded]'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {isGCorAdmin ? (
                <div style={{ marginTop: 10, fontSize: 12, color: '#A8C4DF' }}>
                  <b>What you will be asked to do with each one:</b>
                  <ul style={{ margin: '6px 0 0 18px' }}>
                    {GC_ACTS.map((a) => <li key={a.key}><b>{a.label}</b> &mdash; {a.says}</li>)}
                  </ul>
                  <div style={{ marginTop: 8, color: '#FFD27A' }}>
                    Recording those three acts is <b>not built yet</b>, so there is nothing here to press. A button
                    that did nothing would be a promise this screen cannot keep; this sentence is the truth instead.
                  </div>
                </div>
              ) : null}
            </div>
          );
        })()}
        {/* RULING 263: the national agreements - every member and officer reads them, the same way as his own */}
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-title">National agreements (NCCC rounds - Section 6 settlements)</div>
          <div style={{ fontSize: 12, color: '#8AA4BF', margin: '4px 0 10px' }}>The national agreements between the carriers' National Carriers' Conference Committee and the organization. Open to every member and officer. A tentative agreement is marked tentative until its ratified copy is on file.</div>
          {national === null ? <div style={{ fontSize: 12, color: '#8AA4BF' }}>Loading…</div> : national.length === 0 ? (
            <div style={{ fontSize: 12, color: '#8AA4BF' }}>No national agreement is on file yet.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr>{['Date', 'Agreement', 'Status', ''].map(h => <th key={h} style={{ textAlign: 'left', padding: '6px 8px', color: '#8AA4BF', fontSize: 11, borderBottom: '1px solid #2E4A6B' }}>{h}</th>)}</tr></thead>
              <tbody>{national.map(n => (
                <tr key={n.key}>
                  <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{n.effectiveDate || n.roundYear || ''}</td>
                  <td style={{ padding: '6px 8px' }}>{n.title}{n.awardNo ? <span style={{ color: '#8AA4BF' }}> · {n.awardNo}</span> : null}</td>
                  <td style={{ padding: '6px 8px', color: n.agreementStatus === 'tentative' ? '#E8A020' : '#5FB878', fontWeight: 700, fontSize: 11 }}>{n.agreementStatus === 'tentative' ? 'TENTATIVE' : 'SIGNED'}</td>
                  <td style={{ padding: '6px 8px' }}><button className="btn btn-secondary btn-sm" onClick={async () => { const v = currentVersion(n); const url = v ? await fileUrl(v) : null; setViewer({ title: n.title, url, fileName: v ? v.fileName : null }); }}>Open</button></td>
                </tr>
              ))}</tbody>
            </table>
          )}
          <AgreementMatrices user={user} />   {/* Ruling 310: confirmed and released by the General Chairperson only */}
<Section6Matrix />
<div style={{ marginTop: 8 }}><SlidesButton label="▶ Amtrak Yardmaster rule changes, as slides" build={() => ruleChangesDeck({ agreementTitle: 'Amtrak Yardmaster Agreement', baseRules: AMTRAK_BASE_RULES, mods: AMTRAK_RULE_MODS })} /></div>   {/* Ruling 311 */}
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
          {committees.map(c => (
            <button key={c} className={`btn btn-sm ${filter === c ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setFilter(c)}>{c}</button>
          ))}
        </div>

        {isGCorAdmin ? (
          <div className="alert-banner">
            🛠 Custodian tools: you maintain the agreements for your committee. “Swap in new version” keeps the old copy in history; “Archive” retires without deleting. Every change is logged under your name.
          </div>
        ) : (
          <div className="alert-caution">&#9888; These are the agreements that govern your work. Open any document to read it. Contact your Local Chairperson with questions.</div>
        )}

        {/* PDF VIEWER MODAL */}
        {readDoc && (
          <Modal onClose={() => setReadDoc(null)} title={readDoc.title} wide>
            <AgreementDocument doc={readDoc} />
          </Modal>
        )}
        {viewer && (
          <Modal onClose={() => setViewer(null)} title={viewer.title} wide>
            {viewer.url ? (
              <iframe title={viewer.title} src={viewer.url} style={{ width: '100%', height: '70vh', border: '1px solid #2E4A6B', borderRadius: 6, background: '#fff' }} />
            ) : (
              <div style={{ color: '#4A6280', padding: 20, textAlign: 'center' }}>No document uploaded for this agreement yet.</div>
            )}
            {viewer.url && (
              <div style={{ marginTop: 10, textAlign: 'right' }}>
                <a href={viewer.url} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-sm">Open in new tab ↗</a>
              </div>
            )}
          </Modal>
        )}

        {/* VERSION HISTORY MODAL */}
        {history && history.record && (
          <Modal onClose={() => setHistory(null)} title={'Version history — ' + history.title}>
            {(history.record.versions || []).slice().reverse().map((v, i) => {
              const rs = ratificationState(v);
              const tone = rs.tone === 'ok' ? '#5FB878' : rs.tone === 'bad' ? '#E06C6C' : rs.tone === 'warn' ? '#E8A020' : '#8AA4BF';
              return (
              <div key={i} style={{ background: '#0A1628', border: '1px solid #2E4A6B', borderRadius: 6, padding: '8px 12px', marginBottom: 6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontSize: 12, color: '#F4F6F8' }}>
                    <strong>v{v.version}</strong> — {v.fileName}
                    {v.version === history.record.currentVersion && <span className="badge badge-settlement" style={{ marginLeft: 8 }}>current</span>}
                    <div style={{ fontSize: 10, color: '#8AA4BF' }}>
                      effective {v.effectiveDate || '—'} · uploaded {String(v.uploadedAt || '').slice(0, 10)} by {v.uploadedByName || '—'}
                    </div>
                  </div>
                  <button className="btn btn-secondary btn-sm" onClick={async () => { const url = await fileUrl(v); setViewer({ title: history.title + ' (v' + v.version + ')', url, fileName: v.fileName }); }}>View</button>
                </div>
                {/* WO-001 S10: the ratification record of this version - SS85 */}
                <div style={{ fontSize: 11, color: tone, marginTop: 6 }}>{rs.line}</div>
                {mayRecordRatification(user, history.record) && (!ratForm || ratForm.versionNo !== v.version) && (
                  <button className="btn btn-secondary btn-sm" style={{ marginTop: 4, fontSize: 10 }} onClick={() => setRatForm({ versionNo: v.version, kind: 'system', polledOn: '', votesFor: '', votesAgainst: '', signedOn: '', reportedOn: '', evidence: '', reason: '', note: '' })}>{v.ratification ? 'Correct the ratification record' : 'Record the ratification'}</button>
                )}
                {ratForm && ratForm.versionNo === v.version && (
                  <div style={{ marginTop: 8, padding: 8, border: '1px solid #2E4A6B', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                    <select aria-label="What is being recorded about the agreement" className="form-input" value={ratForm.kind} onChange={e => setRatForm({ ...ratForm, kind: e.target.value })}>{Object.values(RATIFICATION_KINDS).map(k => <option key={k.kind} value={k.kind}>{k.label}</option>)}</select>
                    <div style={{ fontSize: 10, color: '#8AA4BF', alignSelf: 'center' }}>{RATIFICATION_KINDS[ratForm.kind].method ? RATIFICATION_KINDS[ratForm.kind].method + ' (' + RATIFICATION_KINDS[ratForm.kind].clause + ')' : 'the custodian says why, on the record'}</div>
                    {ratForm.kind === 'none' ? (
                      <input aria-label="Why no ratification is required for this document" className="form-input" style={{ gridColumn: '1 / span 2' }} placeholder="Why no ratification is required for this document" value={ratForm.reason} onChange={e => setRatForm({ ...ratForm, reason: e.target.value })} />
                    ) : (
                      <>
                        <input aria-label="Polled on" className="form-input" type="date" title="Poll closed / result known" value={ratForm.polledOn} onChange={e => setRatForm({ ...ratForm, polledOn: e.target.value })} />
                        <input aria-label="Signed on" className="form-input" type="date" title="Signed on (empty if not yet)" value={ratForm.signedOn} onChange={e => setRatForm({ ...ratForm, signedOn: e.target.value })} />
                        <input aria-label="Votes for" className="form-input" type="number" min="0" placeholder="Votes for" value={ratForm.votesFor} onChange={e => setRatForm({ ...ratForm, votesFor: e.target.value })} />
                        <input aria-label="Votes against" className="form-input" type="number" min="0" placeholder="Votes against" value={ratForm.votesAgainst} onChange={e => setRatForm({ ...ratForm, votesAgainst: e.target.value })} />
                        <input aria-label="Reported on" className="form-input" type="date" title="Reported to the Locals on (empty if not yet; due 30 days from the result)" value={ratForm.reportedOn} onChange={e => setRatForm({ ...ratForm, reportedOn: e.target.value })} />
                        <input aria-label="Evidence (tally sheet, minutes, letter…)" className="form-input" placeholder="Evidence (tally sheet, minutes, letter…)" value={ratForm.evidence} onChange={e => setRatForm({ ...ratForm, evidence: e.target.value })} />
                      </>
                    )}
                    {ratErr && <div style={{ gridColumn: '1 / span 2', fontSize: 11, color: '#E06C6C' }}>&#9888; {ratErr}</div>}
                    <button className="btn btn-primary btn-sm" disabled={busy === 'ratify'} onClick={async () => {
                      setRatErr('');
                      const plan = planRatification({ ...ratForm, votesFor: ratForm.votesFor === '' ? NaN : Number(ratForm.votesFor), votesAgainst: ratForm.votesAgainst === '' ? NaN : Number(ratForm.votesAgainst), recordedBy: user.uid, recordedByName: user.name || user.email });
                      if (!plan.ok) { setRatErr(plan.errors.join(' ')); return; }
                      setBusy('ratify');
                      try { await recordRatification(user, history.record, v.version, plan.record); await reloadRecords(); setRatForm(null); setHistory(h => h ? { ...h, record: { ...h.record, versions: (h.record.versions || []).map(x => x.version === v.version ? { ...x, ratification: plan.record } : x) } } : h); }
                      catch (e) { setRatErr('Could not record it: ' + (e.code || e.message)); }
                      setBusy('');
                    }}>Record</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => { setRatForm(null); setRatErr(''); }}>Cancel</button>
                  </div>
                )}
              </div>
              );
            })}
          </Modal>
        )}

        {/* ADD NEW AGREEMENT MODAL */}
        {showAdd && (
          <Modal onClose={() => setShowAdd(false)} title="Add New Agreement">
            <Field label="Title"><input aria-label="e.g. CSX Yardmaster System Agreement (2026)" className="form-input" value={addForm.title} onChange={e => setAddForm({ ...addForm, title: e.target.value })} placeholder="e.g. CSX Yardmaster System Agreement (2026)" /></Field>
            <Field label="Railroad / property"><input aria-label="e.g. CSX Transportation — Former Conrail (Northern Lines)" className="form-input" value={addForm.railroadKey} onChange={e => setAddForm({ ...addForm, railroadKey: e.target.value })} placeholder="e.g. CSX Transportation — Former Conrail (Northern Lines)" /></Field>
            <Field label="Craft"><input aria-label="Craft" className="form-input" value={addForm.craft} onChange={e => setAddForm({ ...addForm, craft: e.target.value })} /></Field>
            <Field label="Effective date"><input aria-label="Effective date" type="date" className="form-input" value={addForm.effectiveDate} onChange={e => setAddForm({ ...addForm, effectiveDate: e.target.value })} /></Field>
            <Field label="Custodian committee"><select aria-label="Select committee" className="form-input" value={addForm.committee || user?.committee || ''} onChange={e => setAddForm({ ...addForm, committee: e.target.value })} disabled={!isAdmin}><option value="">- select committee -</option>{committees.filter(c => c !== 'All').map(c => (<option key={c} value={c}>{c}</option>))}</select></Field>
            <Field label="Agreement file (PDF)"><input aria-label="Attach the agreement (PDF)" type="file" onChange={e => setAddForm({ ...addForm, _file: e.target.files[0] })} /></Field>
            {/* Record 599: ruling 211 - the required attestation */}
            <label style={{ fontSize: 12, color: '#DCE7F2', display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 12 }}>
              <input aria-label="I attest this is the agreement of record" type="checkbox" required checked={addForm._attested === true} onChange={e => setAddForm({ ...addForm, _attested: e.target.checked })} style={{ marginTop: 3 }} />
              <span>{ATTESTATION_TEXT}</span>
            </label>
            <div style={{ fontSize: 11, color: '#8AA4BF', marginBottom: 12 }}>
              Committee: <strong>{addForm.committee || user?.committee || 'none selected'}</strong>. You are recorded as custodian; this action is logged under your name.
            </div>
            <div style={{ textAlign: 'right' }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowAdd(false)} style={{ marginRight: 8 }}>Cancel</button>
              <button className="btn btn-primary btn-sm" disabled={busy === 'create' || addForm._attested !== true} onClick={doCreate}>{busy === 'create' ? 'Uploading…' : 'Create agreement'}</button>
            </div>
          </Modal>
        )}

        {/* AUDIT LOG MODAL (claims filed under an agreement) */}
        {auditAgreement && (
          <Modal onClose={() => setAuditAgreement(null)} title={'Claim Audit Log — ' + auditAgreement.title} wide>
            {loadingAudit ? (
              <div style={{ color: '#8AA4BF', padding: 20, textAlign: 'center' }}>Loading claims…</div>
            ) : auditError ? (
              <div style={{ color: '#E8A020', padding: 20, textAlign: 'center' }}>
                &#9888; <b>The claims could not be read</b> ({String(auditError)}).<br />
                <span style={{ color: '#8AA4BF', fontSize: 12 }}>
                  This is <b>not</b> an empty audit log &mdash; the query was refused, so this page
                  does not know what has been filed under this agreement. Do not read it as quiet.
                </span>
              </div>
            ) : auditClaims.length === 0 ? (
              <div style={{ color: '#4A6280', padding: 20, textAlign: 'center' }}>No claims filed under this agreement.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead><tr style={{ background: '#0A1628' }}>
                  {['Claim #', 'Claimant', 'Status', 'Amount', 'Date'].map(h => <th key={h} style={{ padding: 8, color: '#8AA4BF', textAlign: 'left' }}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {auditClaims.map((claim, i) => (
                    <tr key={claim.id} style={{ background: i % 2 === 0 ? '#0D1B2A' : '#0A1628' }}>
                      <td style={{ padding: 8, color: '#E8A020', fontFamily: 'monospace' }}>{claim.id}</td>
                      <td style={{ padding: 8, color: '#F4F6F8' }}>{claim.claimantName}</td>
                      <td style={{ padding: 8, fontSize: 10, color: '#8AA4BF', textTransform: 'uppercase' }}>{claim.status}</td>
                      <td style={{ padding: 8, color: '#40C070' }}>${parseFloat(claim.totalAmountOT || claim.totalAmountST || claim.amountClaimed || 0).toLocaleString()}</td>
                      <td style={{ padding: 8, color: '#8AA4BF' }}>{claim.incidentDate || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Modal>
        )}

        {filtered.length === 0 && !loading && (
          <div className="empty-state" style={{ marginTop: 40 }}><p>No agreements to show for your account yet.</p></div>
        )}

        {activeItems.map(renderAgreementCard)}

        {archivedItems.length > 0 && (
          <div style={{ marginTop: 28 }}>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setShowArchived(v => !v)}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: '#0A1628', border: '1px solid #2E4A6B' }}
            >
              <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1.5, color: '#E8A020', textTransform: 'uppercase' }}>
                🗄 Archived — {archivedItems.length} agreement{archivedItems.length !== 1 ? 's' : ''}
              </span>
              <span style={{ fontSize: 11, color: '#8AA4BF' }}>{showArchived ? 'Close folder' : 'Open folder'}</span>
            </button>
            {showArchived ? (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 11, color: '#4A6280', marginBottom: 12 }}>
                  Retired, never deleted. Version history is intact and any of these can be restored to the active library.
                </div>
                {archivedItems.map(renderAgreementCard)}
              </div>
            ) : (
              <div style={{ fontSize: 11, color: '#4A6280', marginTop: 6 }}>
                Retired agreements are kept out of the working list so they do not crowd it. Nothing here is deleted.
              </div>
            )}
          </div>
        )}
      </div>

      {openGuide && (
        <Modal title={openGuide.title} onClose={() => setOpenGuide(null)} wide>
          <div style={{ fontSize: 10.5, color: '#8AA4BF', marginBottom: 10 }}>
            Sourced from the governing agreement text under git. Verify against the agreement before relying on any deadline in a live case.
          </div>
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit', fontSize: 12.5, lineHeight: 1.65, color: '#D8E2EE', margin: 0 }}>{openGuide.text}</pre>
        </Modal>
      )}

      {/* REFERENCE GUIDES — LC/GC only */}
      {(isGCorAdmin || user?.role === 'local_chair') && (
        <div style={{ marginTop: 32 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#E8A020', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 4, borderBottom: '2px solid #E8A020', paddingBottom: 6 }}>📚 Representative Reference Guides</div>
          <div style={{ fontSize: 11, color: '#4A6280', marginBottom: 16 }}>Guides for Local and General Chairpersons. Confidential — not visible to members.</div>
          {REFERENCE_GUIDES.map(guide => (
            <div key={guide.id} style={{ background: '#0D1B2A', border: '1px solid #2E4A6B', borderRadius: 8, padding: 16, marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#F4F6F8', marginBottom: 2 }}>{guide.title}</div>
                  <div style={{ fontSize: 11, color: '#8AA4BF', marginBottom: 4 }}>{guide.subtitle}</div>
                  <div style={{ fontSize: 10, color: '#E8A020' }}>{guide.agreement}</div>
                </div>
                <div style={{ fontSize: 10, background: '#1A2A3A', color: '#4A9EE8', padding: '3px 8px', borderRadius: 4, whiteSpace: 'nowrap' }}>{guide.category}</div>
              </div>
              <div style={{ fontSize: 11, color: '#C8D8E8', marginBottom: 10, lineHeight: 1.6 }}>{guide.description}</div>
              <div>{guide.topics.map((topic, i) => <div key={i} style={{ fontSize: 10, color: '#8AA4BF', marginBottom: 2 }}>• {topic}</div>)}</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10 }}>
                <span style={{ fontSize: 10, color: '#4A6280' }}>Last updated: {guide.lastUpdated}</span>
                {guide.pdfUrl
                  ? <a href={guide.pdfUrl} target="_blank" rel="noreferrer" className="btn btn-secondary" style={{ fontSize: 11, padding: '4px 12px' }}>📄 Open Guide</a>
                  : guideText(guide.id)
                    ? <button className="btn btn-secondary" style={{ fontSize: 11, padding: '4px 12px' }} onClick={() => setOpenGuide({ title: guide.title, text: guideText(guide.id) })}>📖 Read Guide</button>
                    : <span style={{ fontSize: 10, color: '#FF6B6B', fontStyle: 'italic' }}>Not yet written — no document exists for this entry</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- small presentational helpers ------------------------------------------
function Modal({ title, onClose, children, wide }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#1B2A4A', border: '1px solid #2E4A6B', borderRadius: 8, padding: 24, width: '90%', maxWidth: wide ? 900 : 560, maxHeight: '85vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#F4F6F8' }}>{title}</div>
          <button className="btn btn-secondary btn-sm" onClick={onClose}>✕ Close</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 11, color: '#8AA4BF', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>{label}</label>
      {children}
    </div>
  );
}
